import Box from '@mui/material/Box';
import { type JSX, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { FormLabel } from 'Components/FormField/FormLabel/FormLabel';
import { UnsavedChangesWarning } from 'Components/Text/UnsavedChangesWarning';
import { ICON_DELETE_REMOVE, ICON_SAVE } from 'Config/Icons';
import { UCRM_USE_CASE_DELEGATES, UCRM_USE_CASE_DESCRIPTION, UCRM_USE_CASE_MANAGER, UCRM_USE_CASE_NAME } from 'Config/Tooltips';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { UCRM_ROLES } from 'Helpers/UserUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { ValidationError } from 'Models/ErrorTypes';
import { FinalizationState } from 'Models/Organization';
import { ThirdPartyService } from 'Models/TPRM';
import { GroupOptionType } from 'Models/Types/GlobalType';
import { BusinessForNonUcrmUser, BusinessResponse, CreateUseCaseRequest, UpdateUseCaseRequest, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';
import { UserResponse } from 'Models/User';

import styles from './ManageUseCaseFormFields.module.css';
import { DeleteUseCaseModal } from '../DeleteUseCaseModal/DeleteUseCaseModal';
import { TprmServiceMapping } from '../TprmServiceMapping/TprmServiceMapping';

/**
 * The props for `ManageUseCaseFormFields`.
 * @param business The business that the use case belongs to.
 * @param hideDeleteButton If `true`, the user will not be able to delete the use case. At the time of writing, this is `true` only for the UCRM Intake page.
 * @param useCase The existing use case to update or delete. If omitted, the form creates a new use case.
 * @param tprmServices All TPRM third-party services. If provided, the user can choose the TPRM services that the use case relies on. If omitted, the field is hidden, and an existing use case's current associations are left unchanged when it is saved.
 */
export interface ManageUseCaseFormFieldsProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    onFormSubmitBegin: () => void;
    onFormSubmitFailed: (errorMessage: string) => void;
    onFormSubmittedSuccessfully: (useCase: UseCase | UseCaseForNonUcrmUser) => Promise<void>;
    business: BusinessResponse | BusinessForNonUcrmUser;
    ucrmApi: ClientUcrmApi;
    userHasFullUcrmAccess: boolean;

    hideDeleteButton?: boolean;
    useCase?: UseCase | UseCaseForNonUcrmUser;
    tprmServices?: ThirdPartyService[];
}

interface FormFieldsState {
    associatedTprmServiceIds: string[];
    name?: string;
    description?: string;
    useCaseManagerUserId?: string;
    delegates?: UserResponse[];
    tags: string[];
}

type RequestActionNoConfirmationNeeded = 'edit' | 'createAsDraft';
type RequestActionConfirmationNeeded = 'delete';

type RequestAndModalState =
    | {
          requestState: 'idle';
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'inProgress';
          action: RequestActionNoConfirmationNeeded | RequestActionConfirmationNeeded;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionNoConfirmationNeeded;
          deleteConfirmationModalIsActive: false;
      }
    | {
          requestState: 'success';
          completedAction: RequestActionConfirmationNeeded;
          deleteConfirmationModalIsActive: boolean;
      }
    | {
          requestState: 'failure';
      };

/**
 * Renders a form for creating a new use case, or updating or deleting an existing use case.
 * Used on the Manage Use Case page as well as on the UCRM Intake page.
 */
export const ManageUseCaseFormFields = (props: ManageUseCaseFormFieldsProps): JSX.Element => {
    const cachedData = useCachedData();

    const [requestAndModalState, setRequestAndModalState] = useState<RequestAndModalState>({ requestState: 'idle', deleteConfirmationModalIsActive: false });
    const [useCaseManager, setUseCaseManager] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.useCase?.vendor_service_manager_user_id)); // Keeps track of the currently selected user in the Use Case Manager drop-down menu.

    const [formFieldsState, setFormFieldsState] = useState<FormFieldsState>({
        associatedTprmServiceIds: props.useCase && 'associated_tprm_services' in props.useCase ? props.useCase.associated_tprm_services.map((service) => service.id) : [],
        name: props.useCase?.name,
        description: props.useCase?.description,
        useCaseManagerUserId: props.useCase?.vendor_service_manager_user_id,
        delegates: props.useCase?.delegates ? cachedData.users.filter((user) => props.useCase?.delegates?.includes(user.cognito_subject)) : undefined,
        tags: props.useCase?.tags ?? [],
    });

    const [hasUnsavedChanges, onFormFieldValuesSaved] = useUnsavedChangesTracking(formFieldsState, false);

    const descriptionAi = useAiContentAdvisor(props.artificialIntelligenceApi, formFieldsState.description ?? '', AiContentAdvisorFeature.UCRM_USE_CASE_DESCRIPTION, props.onFormSubmitFailed);

    const createUseCase = async (irqTargetCompletionDate?: Date): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'createAsDraft' });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const useCase = (await props.ucrmApi.createUseCase(buildCreateRequest(irqTargetCompletionDate), props.business.id)).data;
            await props.onFormSubmittedSuccessfully(useCase);
            setRequestAndModalState({ requestState: 'success', completedAction: 'createAsDraft', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    const updateUseCase = async (): Promise<void> => {
        setRequestAndModalState({ requestState: 'inProgress', action: 'edit' });

        try {
            validateForm();
            props.onFormSubmitBegin();
            const useCase = (await props.ucrmApi.updateUseCase(buildUpdateRequest(), props.business.id, props.useCase!.id)).data;
            await props.onFormSubmittedSuccessfully(useCase);
            setRequestAndModalState({ requestState: 'success', completedAction: 'edit', deleteConfirmationModalIsActive: false });
            onFormFieldValuesSaved(formFieldsState);
        } catch (err) {
            handleRequestError(err);
        }
    };

    const buildCreateRequest = (irqTargetCompletionDate?: Date): CreateUseCaseRequest => {
        return {
            associated_tprm_services: showTprmServiceMapping ? formFieldsState.associatedTprmServiceIds : undefined,
            finalization_state: FinalizationState.DRAFT_IN_PROGRESS,
            name: formFieldsState.name!,
            description: formFieldsState.description!,
            vendor_service_manager_user_id: formFieldsState.useCaseManagerUserId!,
            assessment_due_date: props.useCase?.assessment_workflow_setup?.due_date,
            delegates: formFieldsState.delegates?.map((user) => user.cognito_subject),
            irq_target_completion_date: irqTargetCompletionDate ? jsDateToIso8601(irqTargetCompletionDate) : undefined,
            tags: formFieldsState.tags,
        };
    };

    const buildUpdateRequest = (): UpdateUseCaseRequest => {
        return {
            associated_tprm_services: showTprmServiceMapping ? formFieldsState.associatedTprmServiceIds : undefined,
            name: formFieldsState.name!,
            description: formFieldsState.description!,
            vendor_service_manager_user_id: formFieldsState.useCaseManagerUserId!,
            assessment_due_date: props.useCase?.assessment_workflow_setup?.due_date,
            delegates: formFieldsState.delegates?.map((user) => user.cognito_subject),
            irq_target_completion_date: props.useCase?.assessment_workflow_setup?.irq_target_completion_date,
            tags: formFieldsState.tags,
        };
    };

    const validateForm = (): void => {
        if (!formFieldsState.name) {
            throw new ValidationError('Name is required.');
        }
        if (!formFieldsState.useCaseManagerUserId) {
            throw new ValidationError('Manager is required.');
        }
        if (!formFieldsState.description) {
            throw new ValidationError('Description is required.');
        }
    };

    /** Handles error responses from the API. */
    const handleRequestError = (error: Error): void => {
        props.onFormSubmitFailed(error.message);
        setRequestAndModalState({ requestState: 'failure' });
    };

    /** Handles changes made to general text fields. */
    const handleChange = (event: React.FormEvent<HTMLInputElement>): void => {
        setFormFieldsState({ ...formFieldsState, [event.currentTarget.name]: event.currentTarget.value });
    };

    /** Handles changes made to the Use Case Manager drop-down (select) field. */
    const handleSelectUserChange = (user: UserResponse | undefined, formFieldId: string): void => {
        if (user === undefined) {
            setUseCaseManager(undefined);
            setFormFieldsState({ ...formFieldsState, [formFieldId]: undefined });
        } else {
            setUseCaseManager(user);
            setFormFieldsState({ ...formFieldsState, [formFieldId]: user.cognito_subject });
        }
    };

    /** Handles changes made to the Use Case Delegates drop-down (select) field. */
    const handleSelectUsersChange = (users: UserResponse[] | undefined, formFieldId: string): void => {
        setFormFieldsState({ ...formFieldsState, [formFieldId]: users });
    };

    const useCase = props.useCase;
    const formDisabled = requestAndModalState.requestState === 'inProgress' || (requestAndModalState.requestState === 'success' && requestAndModalState.completedAction === 'delete');
    const showTprmServiceMapping = props.tprmServices !== undefined;

    return (
        <>
            {(requestAndModalState.requestState === 'idle' || requestAndModalState.requestState === 'success') && requestAndModalState.deleteConfirmationModalIsActive && useCase && <DeleteUseCaseModal hideModal={() => setRequestAndModalState({ ...requestAndModalState, deleteConfirmationModalIsActive: false })} onSuccess={async () => setRequestAndModalState({ requestState: 'success', completedAction: 'delete', deleteConfirmationModalIsActive: true })} useCase={useCase} ucrmApi={props.ucrmApi} userHasFullUcrmAccess={props.userHasFullUcrmAccess} />}
            <Box component="form" noValidate>
                <fieldset disabled={formDisabled}>
                    <div className={styles.formFieldGroup}>
                        <div className={styles.formFieldContainer}>
                            <FormFieldText formFieldType="text" handleChange={handleChange} formFieldId="name" formFieldLabel="Name" required tooltip={UCRM_USE_CASE_NAME} value={formFieldsState.name || ''} />
                        </div>
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldUserSelect rolesRestriction={UCRM_ROLES} onUserSelected={handleSelectUserChange} formFieldId="useCaseManagerUserId" selectedUser={useCaseManager} formFieldLabel="Manager" tooltip={UCRM_USE_CASE_MANAGER} required disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldUserMultiSelect rolesRestriction={UCRM_ROLES} onUsersSelected={handleSelectUsersChange} formFieldId="delegates" selectedUsers={formFieldsState.delegates} formFieldLabel="Delegates" tooltip={UCRM_USE_CASE_DELEGATES} disabled={formDisabled} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldTextAreaAi handleChange={handleChange} formFieldId="description" formFieldLabel="Description" rows={5} required tooltip={UCRM_USE_CASE_DESCRIPTION} value={formFieldsState.description || ''} aiContentAdvisorValues={descriptionAi} />
                    </div>
                    <div className={styles.formFieldContainer}>
                        <FormFieldMultiOptionSelect
                            defaultSelectedOptions={cachedData
                                .getTagCategoriesGroupedOptions()
                                .map((group) => group.options)
                                .flat()
                                .filter((option) => formFieldsState.tags.includes(option.value as string))}
                            formFieldLabel="Tags"
                            formFieldId="tags"
                            handleChange={(value: GroupOptionType[]) => setFormFieldsState((current) => ({ ...current, tags: value.map((tag) => tag.value as string) }))}
                            options={cachedData.getTagCategoriesGroupedOptions()}
                            accessibilityLabel="Tags"
                            disabled={formDisabled}
                        />
                    </div>
                    {showTprmServiceMapping && (
                        <div className={styles.formFieldContainer}>
                            <FormLabel>Third-Party Services</FormLabel>
                            <TprmServiceMapping tprmServices={props.tprmServices!} currentlyMappedServiceIds={formFieldsState.associatedTprmServiceIds} handleChange={(serviceIds) => setFormFieldsState((current) => ({ ...current, associatedTprmServiceIds: serviceIds }))} />
                        </div>
                    )}
                    <div className={'modalFormButtonContainer'}>
                        <>
                            {props.useCase && !props.hideDeleteButton && (
                                <div className={styles.deleteButton}>
                                    <Button fontAwesomeImage={ICON_DELETE_REMOVE} onClick={() => setRequestAndModalState({ requestState: 'idle', deleteConfirmationModalIsActive: true })} variant="danger" isLoading={false} loadingText="Deleting...">
                                        Delete
                                    </Button>
                                </div>
                            )}
                            <Button
                                variant="primary"
                                fontAwesomeImage={ICON_SAVE}
                                isLoading={requestAndModalState.requestState === 'inProgress' && requestAndModalState.action === (props.useCase ? 'edit' : 'createAsDraft')}
                                loadingText="Saving..."
                                onClick={() => {
                                    if (props.useCase) {
                                        updateUseCase();
                                    } else {
                                        createUseCase();
                                    }
                                }}
                            >
                                Save
                            </Button>
                        </>
                    </div>
                    <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
                </fieldset>
            </Box>
        </>
    );
};
